import React, { useEffect, useLayoutEffect, useMemo, useRef, useState, useCallback } from 'react';
import { tenantApi } from '../../api/tenantApi';
import { Panel, Loader, EmptyState, useToast, errText } from '../ui';

// ============================================================================
// LoginDashboard
//
// Source: GET /api/Default/LoginLogReportJson?clientCode=&page=&pageSize=
//
// Tabs
//   pivot_matrix   count(loginId) by year > quarter > month  x  client
//   client_summary one row per client: totals, users, first/last/peak month
//   raw_log        server-side paginated log for ONE client (editable page size)
//
// The API returns raw rows for a single client, page by page, so the first two
// tabs are built by run_scan: it walks every page of every selected client and
// aggregates client-side. Nothing is selected or fetched until you ask for it.
// ============================================================================

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTH_FULL = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
const MAX_PAGE_SIZE = 1000;
const DEFAULT_PAGE_SIZE = 25;
const DEFAULT_CHUNK = 20;
const PRESETS = [10, 25, 50, 100, 250];
const FETCH_CONCURRENCY = 4;
const MAX_CHIPS = 12;

// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------
const toList = (res) =>
  Array.isArray(res) ? res
    : Array.isArray(res?.data) ? res.data
    : Array.isArray(res?.result) ? res.result
    : Array.isArray(res?.tenants) ? res.tenants
    : [];

const codeOf = (t) => (typeof t === 'string' ? t : t?.clientCode ?? t?.ClientCode ?? t?.code ?? null);

// logDate looks like "19/09/2026 11:19:12" (dd/MM/yyyy)
function parseLogDate(s) {
  const m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})/.exec(s || '');
  if (!m) return null;
  return { m: Number(m[2]), y: Number(m[3]) };
}

const quarterOf = (m) => Math.ceil(m / 3);
const isOk = (s) => /login\s*suc/i.test(s || ''); // "LoginSuccessfully" / "Login Sucessfully!"
const fmt = (n) => Number(n || 0).toLocaleString('en-US');
const pctOf = (n, d) => (d > 0 ? (n / d) * 100 : 0);

const bar = (done, total, width = 20) => {
  const p = total > 0 ? Math.min(1, done / total) : 0;
  const n = Math.round(p * width);
  return `[${'#'.repeat(n)}${'.'.repeat(width - n)}]`;
};

function assertOk(res) {
  if (res && res.success === false) {
    throw new Error(res.errorMessages?.[0] || 'API reported success:false');
  }
  return res;
}

// Pulls every page for one client and folds it into { "y|m": count }.
async function scanClient(clientCode, chunk, isCancelled, onProgress) {
  const first = assertOk(await tenantApi.getLoginLogReport(clientCode, 1, chunk));
  const total = first?.totalRecords ?? (first?.data || []).length;
  const totalPages = first?.totalPages || 1;

  const counts = {};
  const users = new Set();
  let fetched = 0;
  let unparsed = 0;

  const absorb = (res) => {
    (res?.data || []).forEach((r) => {
      fetched += 1;
      users.add(String(r.userId ?? r.username ?? ''));
      const d = parseLogDate(r.logDate);
      if (!d) { unparsed += 1; return; }
      const key = `${d.y}|${d.m}`;
      counts[key] = (counts[key] || 0) + 1;
    });
  };

  absorb(first);
  onProgress(fetched, total);

  let nextPage = 2;
  let failure = null;
  const worker = async () => {
    while (!failure && !isCancelled()) {
      const p = nextPage;
      nextPage += 1;
      if (p > totalPages) return;
      try {
        const res = assertOk(await tenantApi.getLoginLogReport(clientCode, p, chunk));
        absorb(res);
        onProgress(fetched, total);
      } catch (err) {
        failure = err;
      }
    }
  };
  const workers = Math.min(FETCH_CONCURRENCY, Math.max(totalPages - 1, 0));
  await Promise.all(Array.from({ length: workers }, worker));
  if (failure) throw failure;

  return { counts, users: users.size, total: fetched, serverTotal: total, unparsed };
}

// year > quarter > month tree (ascending) with per-client counts on every node
function buildPivot(clients, byClient) {
  const mk = (id, label, level, order) => ({ id, label, level, order, counts: {}, total: 0, children: new Map(), kids: [] });
  const root = mk('root', '', 0, 0);
  const bump = (node, c, n) => {
    node.counts[c] = (node.counts[c] || 0) + n;
    node.total += n;
  };
  const child = (parent, id, label, level, order) => {
    let n = parent.children.get(id);
    if (!n) {
      n = mk(id, label, level, order);
      parent.children.set(id, n);
    }
    return n;
  };

  let max = 0;
  clients.forEach((c) => {
    Object.entries(byClient[c].counts).forEach(([key, n]) => {
      const [y, m] = key.split('|').map(Number);
      const q = quarterOf(m);
      const yn = child(root, `y${y}`, String(y), 1, y);
      const qn = child(yn, `y${y}q${q}`, `Q${q}  (${MONTHS[(q - 1) * 3]}–${MONTHS[q * 3 - 1]})`, 2, q);
      const mn = child(qn, `y${y}q${q}m${m}`, MONTH_FULL[m - 1], 3, m);
      mn.full = `${MONTHS[m - 1]} ${y}`;
      mn.sortKey = y * 100 + m;
      [root, yn, qn, mn].forEach((node) => bump(node, c, n));
      if (n > max) max = n;
    });
  });

  const leaves = [];
  const levelMax = { 1: 0, 2: 0, 3: 0 };
  const finalize = (node) => {
    node.kids = [...node.children.values()].sort((a, b) => a.order - b.order);
    if (node.level > 0) levelMax[node.level] = Math.max(levelMax[node.level], node.total);
    if (!node.kids.length && node.level === 3) leaves.push(node);
    node.kids.forEach(finalize);
  };
  finalize(root);
  leaves.sort((a, b) => a.sortKey - b.sortKey);

  const peak = leaves.reduce((best, l) => (!best || l.total > best.total ? l : best), null);
  return { root, clients, max, leaves, peak, levelMax };
}

function flatten(node, collapsed, desc, out = []) {
  const kids = desc ? [...node.kids].reverse() : node.kids;
  kids.forEach((k) => {
    out.push(k);
    if (k.kids.length && !collapsed.has(k.id)) flatten(k, collapsed, desc, out);
  });
  return out;
}

// per-client overview rows
function buildClientStats(clients, byClient) {
  return clients
    .map((code) => {
      const entries = Object.entries(byClient[code].counts)
        .map(([k, n]) => {
          const [y, m] = k.split('|').map(Number);
          return { y, m, n };
        })
        .sort((a, b) => a.y - b.y || a.m - b.m);
      const total = entries.reduce((s, e) => s + e.n, 0);
      const peak = entries.reduce((b, e) => (!b || e.n > b.n ? e : b), null);
      const label = (e) => (e ? `${MONTHS[e.m - 1]} ${e.y}` : '—');
      return {
        code,
        total,
        users: byClient[code].users,
        months: entries.length,
        first: label(entries[0]),
        last: label(entries[entries.length - 1]),
        peak: peak ? `${label(peak)} (${fmt(peak.n)})` : '—',
      };
    })
    .sort((a, b) => b.total - a.total);
}

// ---------------------------------------------------------------------------
// small components
// ---------------------------------------------------------------------------

// Number input with a floating label. Commits on Enter / blur, clamps to min..max.
function NumField({ label, value, min = 1, max, onCommit, disabled, width = 120 }) {
  const [draft, setDraft] = useState(String(value));
  useEffect(() => { setDraft(String(value)); }, [value]);

  const commit = () => {
    const n = parseInt(draft, 10);
    if (Number.isNaN(n)) { setDraft(String(value)); return; }
    const v = Math.min(max ?? Infinity, Math.max(min, n));
    setDraft(String(v));
    if (v !== value) onCommit(v);
  };

  return (
    <label className="ld-field" style={{ width }}>
      <span className="ld-field__label">{label}</span>
      <input
        className="ld-input"
        type="number"
        inputMode="numeric"
        min={min}
        max={max}
        value={draft}
        disabled={disabled}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
      />
    </label>
  );
}

// Multi-select dropdown: search, select all / unselect all, add-by-code.
// The popover is position:fixed so a Panel with overflow:hidden can't clip it.
function ClientPicker({ codes, selected, onChange, onAdd, disabled }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [newCode, setNewCode] = useState('');
  const [pos, setPos] = useState({ top: 0, left: 0, width: 340 });
  const btnRef = useRef(null);
  const panelRef = useRef(null);

  const place = useCallback(() => {
    const r = btnRef.current?.getBoundingClientRect();
    if (!r) return;
    const width = Math.max(r.width, 340);
    const left = Math.max(8, Math.min(r.left, window.innerWidth - width - 8));
    setPos({ top: r.bottom + 6, left, width });
  }, []);

  useLayoutEffect(() => {
    if (!open) return undefined;
    place();
    const onDown = (e) => {
      if (panelRef.current?.contains(e.target) || btnRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, place]);

  useEffect(() => { if (disabled) setOpen(false); }, [disabled]);

  const needle = q.trim().toLowerCase();
  const filtered = needle ? codes.filter((c) => c.toLowerCase().includes(needle)) : codes;
  const allOn = filtered.length > 0 && filtered.every((c) => selected.has(c));
  const anyOn = filtered.some((c) => selected.has(c));

  const toggle = (c) => {
    const next = new Set(selected);
    if (next.has(c)) next.delete(c); else next.add(c);
    onChange(next);
  };
  const selectAll = () => onChange(new Set([...selected, ...filtered]));
  const unselectAll = () => onChange(new Set([...selected].filter((c) => !filtered.includes(c))));

  const submitNew = () => {
    const v = newCode.trim();
    if (!v) return;
    onAdd(v);
    setNewCode('');
  };

  return (
    <>
      <label className="ld-field" style={{ width: 260 }}>
        <span className="ld-field__label">clients</span>
        <button
          ref={btnRef}
          type="button"
          className="ld-select ld-pick__btn"
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
        >
          <span className={selected.size ? '' : 'ld-mono-dim'}>
            {selected.size ? `${selected.size} of ${codes.length} selected` : 'none selected'}
          </span>
          <span className="ld-pick__caret">{open ? '▴' : '▾'}</span>
        </button>
      </label>

      {open && (
        <div ref={panelRef} className="ld-pick__panel" style={{ top: pos.top, left: pos.left, width: pos.width }}>
          <div className="ld-pick__head">
            <input
              className="ld-input ld-pick__search"
              autoFocus
              placeholder="filter clients..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
          <div className="ld-pick__bulk">
            <button type="button" className="term-btn term-btn--sm" disabled={allOn || !filtered.length} onClick={selectAll}>
              select_all{needle ? ' (filtered)' : ''}
            </button>
            <button type="button" className="term-btn term-btn--sm term-btn--ghost" disabled={!anyOn} onClick={unselectAll}>
              unselect_all{needle ? ' (filtered)' : ''}
            </button>
          </div>

          <div className="ld-pick__list" role="listbox" aria-multiselectable="true">
            {filtered.length === 0 && (
              <div className="ld-pick__empty">{codes.length ? 'no client matches the filter' : 'no clients loaded — add one below'}</div>
            )}
            {filtered.map((c) => (
              <label key={c} className="ld-pick__item">
                <input type="checkbox" checked={selected.has(c)} onChange={() => toggle(c)} />
                <span>{c}</span>
              </label>
            ))}
          </div>

          <div className="ld-pick__foot">
            <input
              className="ld-input ld-pick__search"
              placeholder="add client code, press Enter"
              value={newCode}
              onChange={(e) => setNewCode(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') submitNew(); }}
            />
            <button type="button" className="term-btn term-btn--sm" disabled={!newCode.trim()} onClick={submitNew}>add</button>
          </div>
        </div>
      )}
    </>
  );
}

const CSS = `
.ld-root{--ld-heat:57,255,120;--ld-line:var(--border,#17683a);--ld-dim:var(--text-dim,#5f8f72);--ld-bg:var(--panel,#060d0a);--ld-solid:#050a08}
.ld-toolbar{display:flex;flex-wrap:wrap;gap:14px;align-items:center;margin-bottom:14px}
.ld-toolbar--end{margin-left:auto}
.ld-dim{color:var(--ld-dim);font-size:12px}
.ld-hint{color:var(--ld-dim);font-size:11.5px;margin:0}
.ld-mono-dim{color:var(--ld-dim)}

/* fields */
.ld-field{position:relative;display:inline-block}
.ld-field__label{position:absolute;top:-8px;left:10px;z-index:1;padding:0 4px;font-size:10.5px;line-height:1;color:var(--green,#3dff7f);background:var(--ld-bg)}
.ld-input,.ld-select{width:100%;box-sizing:border-box;padding:11px 10px 8px;font:inherit;font-size:13.5px;color:var(--text,#d7ffe6);background:transparent;border:1px solid var(--ld-line);border-radius:4px}
.ld-input:focus,.ld-select:focus{outline:none;border-color:var(--green,#3dff7f)}
.ld-input:disabled,.ld-select:disabled{opacity:.45}
.ld-select option{background:var(--ld-solid);color:#d7ffe6}
.ld-check{display:inline-flex;align-items:center;gap:6px;font-size:12px;color:var(--text,#d7ffe6);cursor:pointer}

/* client dropdown */
.ld-pick__btn{display:flex;align-items:center;justify-content:space-between;text-align:left;cursor:pointer}
.ld-pick__caret{color:var(--green,#3dff7f)}
.ld-pick__panel{position:fixed;z-index:1000;background:var(--ld-solid);border:1px solid var(--green,#3dff7f);border-radius:4px;box-shadow:0 12px 32px rgba(0,0,0,.6)}
.ld-pick__head{padding:10px 10px 0}
.ld-pick__search{padding:8px 10px}
.ld-pick__bulk{display:flex;gap:8px;padding:10px;border-bottom:1px solid var(--ld-line)}
.ld-pick__list{max-height:250px;overflow:auto}
.ld-pick__item{display:flex;align-items:center;gap:10px;padding:7px 12px;font-size:12.5px;cursor:pointer}
.ld-pick__item:hover{background:rgba(var(--ld-heat),.08)}
.ld-pick__empty{padding:14px 12px;font-size:12px;color:var(--ld-dim)}
.ld-pick__foot{display:flex;gap:8px;padding:10px;border-top:1px solid var(--ld-line)}

/* chips */
.ld-chips{display:flex;flex-wrap:wrap;gap:8px;align-items:center;margin-bottom:14px}
.ld-chip{font:inherit;font-size:12px;padding:3px 9px;color:var(--green,#3dff7f);background:rgba(var(--ld-heat),.08);border:1px solid var(--green,#3dff7f);border-radius:3px;cursor:pointer}
.ld-chip:disabled{cursor:not-allowed;opacity:.5}

/* scan log */
.ld-term{font-size:12px;line-height:1.75;padding:10px 14px;background:rgba(0,0,0,.35);border:1px solid var(--ld-line);border-radius:4px;overflow-x:auto;white-space:pre}
.ld-ok{color:var(--green,#3dff7f)}
.ld-err{color:var(--red,#ff5c5c)}
.ld-run{color:var(--amber,#ffb454)}
.ld-q{color:var(--ld-dim)}

/* stats */
.ld-stats{display:grid;grid-template-columns:repeat(auto-fit,minmax(150px,1fr));gap:10px;margin-bottom:16px}
.ld-stat{padding:10px 12px;border:1px solid var(--ld-line);border-radius:4px}
.ld-stat__k{font-size:10.5px;color:var(--ld-dim)}
.ld-stat__v{margin-top:4px;font-size:20px;color:var(--green,#3dff7f);font-variant-numeric:tabular-nums}
.ld-stat__v--sm{font-size:14px;line-height:28px}

/* tabs */
.ld-tabs{display:flex;gap:4px;margin:18px 0 12px;border-bottom:1px solid var(--ld-line)}
.ld-tab{font:inherit;font-size:12.5px;padding:8px 14px;color:var(--ld-dim);background:transparent;border:0;border-bottom:2px solid transparent;cursor:pointer}
.ld-tab.on{color:var(--green,#3dff7f);border-bottom-color:var(--green,#3dff7f)}

/* shared grid look (summary + raw) */
.ld-tablewrap{max-height:65vh;overflow:auto;border:1px solid var(--ld-line);border-radius:4px}
.ld-grid{width:100%;border-collapse:separate;border-spacing:0;font-size:12.5px}
.ld-grid th,.ld-grid td{padding:7px 12px;text-align:left;white-space:nowrap;border-bottom:1px solid rgba(var(--ld-heat),.08)}
.ld-grid thead th{position:sticky;top:0;z-index:2;background:var(--ld-solid);color:var(--green,#3dff7f);font-weight:600;border-bottom:1px solid var(--ld-line)}
.ld-grid tbody tr:nth-child(even) td{background-color:rgba(var(--ld-heat),.025)}
.ld-grid tbody tr:hover td{background-color:rgba(var(--ld-heat),.09)}
.ld-grid tfoot td{position:sticky;bottom:0;background:var(--ld-solid);border-top:1px solid var(--ld-line);font-weight:700;color:var(--green,#3dff7f)}
.ld-grid .r{text-align:right;font-variant-numeric:tabular-nums}
.ld-grid .id{color:var(--ld-dim);font-variant-numeric:tabular-nums}
.ld-badge{display:inline-block;padding:1px 8px;font-size:11.5px;border:1px solid currentColor;border-radius:10px}

/* bars */
.ld-bar{display:flex;align-items:center;justify-content:flex-end;gap:8px}
.ld-minibar{flex:0 0 70px;height:6px;background:rgba(var(--ld-heat),.10);border-radius:3px;overflow:hidden}
.ld-minibar>i{display:block;height:100%;background:var(--green,#3dff7f);opacity:.75}

/* pivot */
.ld-pivot{width:100%;border-collapse:separate;border-spacing:0;font-size:12.5px}
.ld-pivot th,.ld-pivot td{padding:6px 12px;white-space:nowrap;border-bottom:1px solid rgba(var(--ld-heat),.08)}
.ld-pivot thead th{position:sticky;z-index:3;background:var(--ld-solid);color:var(--green,#3dff7f);text-align:right;font-weight:600}
.ld-pivot thead tr:first-child th{top:0;font-size:10.5px;color:var(--ld-dim);font-weight:400;border-bottom:0}
.ld-pivot thead tr:nth-child(2) th{top:26px;border-bottom:1px solid var(--ld-line)}
.ld-pivot th:first-child,.ld-pivot td:first-child{position:sticky;left:0;z-index:2;text-align:left;background-color:var(--ld-solid);min-width:210px;border-right:1px solid var(--ld-line)}
.ld-pivot thead th:first-child{z-index:4}
.ld-pivot th.grp{text-align:center;border-bottom:1px dashed var(--ld-line)}
.ld-pivot td.num{text-align:right;min-width:64px;font-variant-numeric:tabular-nums}
.ld-pivot td.tot{min-width:170px;border-left:1px solid var(--ld-line)}
.ld-pivot th.tot{border-left:1px solid var(--ld-line)}
.ld-pivot .is-hot{box-shadow:inset 0 0 0 9999px rgba(var(--ld-heat),.07)}
.ld-pivot tbody tr:hover td{box-shadow:inset 0 0 0 9999px rgba(var(--ld-heat),.06)}
.ld-pivot tfoot td{position:sticky;bottom:0;z-index:3;background-color:var(--ld-solid);border-top:1px solid var(--ld-line);font-weight:700;color:var(--green,#3dff7f)}
.ld-pivot tfoot td:first-child{z-index:4}
.ld-l1 td{font-weight:700;background-image:linear-gradient(rgba(var(--ld-heat),.18),rgba(var(--ld-heat),.18));border-top:1px solid var(--ld-line)}
.ld-l1 td:first-child{border-left:3px solid var(--green,#3dff7f)}
.ld-l2 td{font-weight:600;background-image:linear-gradient(rgba(var(--ld-heat),.07),rgba(var(--ld-heat),.07))}
.ld-l2 td:first-child{border-left:3px solid var(--ld-line)}
.ld-l3 td:first-child{border-left:3px solid transparent}
.ld-tog{font:inherit;font-size:12px;padding:0;margin-right:8px;color:var(--amber,#ffb454);background:none;border:0;cursor:pointer}
.ld-tog--leaf{color:var(--ld-dim);cursor:default}
.ld-tag{margin-left:10px;font-size:10px;font-weight:400;color:var(--ld-dim)}
.ld-dot{color:rgba(var(--ld-heat),.22)}
.ld-legend{display:flex;flex-wrap:wrap;gap:6px 18px;margin:10px 0 0;font-size:11.5px;color:var(--ld-dim)}
.ld-swatch{display:inline-block;width:10px;height:10px;margin-right:6px;vertical-align:-1px;border-radius:2px}

/* pager */
.ld-pager{display:flex;flex-wrap:wrap;gap:14px;align-items:center;justify-content:space-between;margin-top:16px;padding-top:14px;border-top:1px solid var(--ld-line)}
.ld-pager__group{display:flex;flex-wrap:wrap;gap:10px;align-items:center}
.ld-preset{font:inherit;font-size:11.5px;padding:4px 8px;color:var(--ld-dim);background:transparent;border:1px solid var(--ld-line);border-radius:3px;cursor:pointer}
.ld-preset.on{color:var(--green,#3dff7f);border-color:var(--green,#3dff7f)}
.ld-alert{margin-bottom:12px;padding:8px 12px;font-size:12px;color:var(--red,#ff5c5c);border:1px solid var(--red,#ff5c5c);border-radius:4px}
@media (max-width:700px){.ld-toolbar--end{margin-left:0}}
`;

// ---------------------------------------------------------------------------
// page
// ---------------------------------------------------------------------------
export default function LoginDashboard() {
  const toast = useToast();
  const [tab, setTab] = useState('pivot'); // pivot | clients | raw

  // ---- targets (nothing selected initially) ----
  const [codes, setCodes] = useState([]);
  const [selected, setSelected] = useState(() => new Set());
  const [tenantsLoading, setTenantsLoading] = useState(true);
  const [chunk, setChunk] = useState(DEFAULT_CHUNK);

  // ---- scan ----
  const [scanning, setScanning] = useState(false);
  const [scanOrder, setScanOrder] = useState([]);
  const [scanStatus, setScanStatus] = useState({});
  const [byClient, setByClient] = useState({});
  const scanSeq = useRef(0);

  // ---- pivot view options ----
  const [collapsed, setCollapsed] = useState(() => new Set());
  const [newestFirst, setNewestFirst] = useState(false);
  const [hideEmpty, setHideEmpty] = useState(false);
  const [hoverCol, setHoverCol] = useState(-1);

  // ---- raw log ----
  const [rawClient, setRawClient] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [raw, setRaw] = useState(null);
  const [rawLoading, setRawLoading] = useState(false);
  const [rawErr, setRawErr] = useState('');
  const [grep, setGrep] = useState('');
  const [reloadTick, setReloadTick] = useState(0);
  const rawSeq = useRef(0);

  // stop any running scan when leaving the page
  useEffect(() => () => { scanSeq.current += 1; }, []);

  // ---- tenants -> dropdown options (no auto-selection) ----
  useEffect(() => {
    (async () => {
      try {
        const res = await tenantApi.getAllTenants();
        const list = [...new Set(toList(res).map(codeOf).filter(Boolean))];
        setCodes((prev) => [...new Set([...list, ...prev])]);
      } catch (err) {
        toast.error(`Could not load tenants: ${errText(err)} — add client codes manually in the dropdown.`);
      } finally {
        setTenantsLoading(false);
      }
    })();
  }, []); // eslint-disable-line

  const addCode = (v) => {
    setCodes((prev) => (prev.includes(v) ? prev : [...prev, v]));
    setSelected((s) => new Set(s).add(v));
  };
  const removeSelected = (c) =>
    setSelected((s) => {
      const next = new Set(s);
      next.delete(c);
      return next;
    });

  // ---- run / abort scan ----
  const runScan = async () => {
    const targets = codes.filter((c) => selected.has(c));
    if (!targets.length) {
      toast.error('Select at least one client first.');
      return;
    }
    const seq = ++scanSeq.current;
    const isCancelled = () => seq !== scanSeq.current;

    setScanning(true);
    setByClient({});
    setScanOrder(targets);
    setScanStatus(Object.fromEntries(targets.map((c) => [c, { state: 'queued', fetched: 0, total: 0 }])));

    for (const code of targets) {
      if (isCancelled()) break;
      setScanStatus((s) => ({ ...s, [code]: { state: 'running', fetched: 0, total: 0 } }));
      try {
        const result = await scanClient(code, chunk, isCancelled, (fetched, total) => {
          if (!isCancelled()) setScanStatus((s) => ({ ...s, [code]: { state: 'running', fetched, total } }));
        });
        if (isCancelled()) break;
        setByClient((b) => ({ ...b, [code]: result }));
        setScanStatus((s) => ({
          ...s,
          [code]: { state: 'done', fetched: result.total, total: result.serverTotal, unparsed: result.unparsed },
        }));
      } catch (err) {
        if (isCancelled()) break;
        setScanStatus((s) => ({ ...s, [code]: { state: 'error', fetched: 0, total: 0, msg: errText(err) } }));
      }
    }
    if (!isCancelled()) setScanning(false);
  };

  const abortScan = () => {
    scanSeq.current += 1;
    setScanning(false);
    setScanStatus((s) => {
      const next = { ...s };
      Object.keys(next).forEach((k) => {
        if (next[k].state === 'running' || next[k].state === 'queued') next[k] = { ...next[k], state: 'aborted' };
      });
      return next;
    });
  };

  // ---- derived data ----
  const scanned = useMemo(() => scanOrder.filter((c) => byClient[c]), [scanOrder, byClient]);
  const pivot = useMemo(() => buildPivot(scanned, byClient), [scanned, byClient]);
  const clientStats = useMemo(() => buildClientStats(scanned, byClient), [scanned, byClient]);
  const flatRows = useMemo(() => flatten(pivot.root, collapsed, newestFirst), [pivot, collapsed, newestFirst]);
  const cols = useMemo(
    () => (hideEmpty ? pivot.clients.filter((c) => (pivot.root.counts[c] || 0) > 0) : pivot.clients),
    [pivot, hideEmpty]
  );

  const yearIds = pivot.root.kids.map((y) => y.id);
  const quarterIds = pivot.root.kids.flatMap((y) => y.kids.map((q) => q.id));
  const toggleNode = (id) =>
    setCollapsed((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });

  const totalUsers = scanned.reduce((n, c) => n + (byClient[c]?.users || 0), 0);
  const firstLeaf = pivot.leaves[0];
  const lastLeaf = pivot.leaves[pivot.leaves.length - 1];
  const selList = codes.filter((c) => selected.has(c));

  const exportCsv = () => {
    const q = (v) => `"${String(v).replace(/"/g, '""')}"`;
    const lines = [['year', 'quarter', 'month', ...pivot.clients, 'total'].map(q).join(',')];
    pivot.root.kids.forEach((y) =>
      y.kids.forEach((qn) =>
        qn.kids.forEach((m) =>
          lines.push(
            [y.label, `Q${qn.order}`, m.label, ...pivot.clients.map((c) => m.counts[c] || 0), m.total].map(q).join(',')
          )
        )
      )
    );
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `login_pivot_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ---- raw log loader (server-side pagination) ----
  useEffect(() => {
    if (tab !== 'raw' || !rawClient) return;
    const seq = ++rawSeq.current;
    setRawLoading(true);
    setRawErr('');
    (async () => {
      try {
        const res = assertOk(await tenantApi.getLoginLogReport(rawClient, page, pageSize));
        if (seq === rawSeq.current) setRaw(res);
      } catch (err) {
        if (seq === rawSeq.current) { setRaw(null); setRawErr(errText(err)); }
      } finally {
        if (seq === rawSeq.current) setRawLoading(false);
      }
    })();
  }, [tab, rawClient, page, pageSize, reloadTick]);

  const changeRawClient = (c) => { setRawClient(c); setPage(1); setRaw(null); setRawErr(''); };
  const changePageSize = (n) => { setPageSize(n); setPage(1); };

  const totalPages = raw?.totalPages || 1;
  const totalRecords = raw?.totalRecords ?? 0;
  const rawRows = raw?.data || [];
  const rangeFrom = rawRows.length ? (page - 1) * pageSize + 1 : 0;
  const rangeTo = rawRows.length ? rangeFrom + rawRows.length - 1 : 0;

  const shownRows = useMemo(() => {
    const needle = grep.trim().toLowerCase();
    if (!needle) return rawRows;
    return rawRows.filter((r) =>
      [r.loginId, r.username, r.employeeName, r.userId, r.branchId, r.logStatus, r.logDate, r.ipAddress]
        .join(' ')
        .toLowerCase()
        .includes(needle)
    );
  }, [rawRows, grep]);

  // ---- render ----
  return (
    <div className="ld-root">
      <style>{CSS}</style>

      <h1 className="page-title">login_dashboard</h1>
      <p className="page-sub">
        GET /api/Default/LoginLogReportJson — count(loginId) by year / quarter / month per client
      </p>

      {/* ------------------------------------------------ targets */}
      <Panel title="// TARGETS">
        {tenantsLoading && <Loader label="enumerating tenants" />}

        {!tenantsLoading && (
          <>
            <div className="ld-toolbar">
              <ClientPicker
                codes={codes}
                selected={selected}
                onChange={setSelected}
                onAdd={addCode}
                disabled={scanning}
              />
              {/* <button
                className="term-btn term-btn--sm"
                disabled={scanning || !codes.length || selected.size === codes.length}
                onClick={() => setSelected(new Set(codes))}
              >
                select_all
              </button>
              <button
                className="term-btn term-btn--sm term-btn--ghost"
                disabled={scanning || !selected.size}
                onClick={() => setSelected(new Set())}
              >
                unselect_all
              </button> */}

              <div className="ld-toolbar ld-toolbar--end" style={{ marginBottom: 0 }}>
                <NumField
                  label="fetch_chunk_size"
                  value={chunk}
                  min={1}
                  max={MAX_PAGE_SIZE}
                  width={160}
                  disabled={scanning}
                  onCommit={setChunk}
                />
                {!scanning ? (
                  <button className="term-btn term-btn--solid" disabled={!selected.size} onClick={runScan}>
                    ▶ run_scan ({selected.size})
                  </button>
                ) : (
                  <button className="term-btn" onClick={abortScan}>■ abort</button>
                )}
              </div>
            </div>

            <div className="ld-chips">
              {selList.length === 0 && <span className="ld-dim">no clients selected — open the dropdown to choose</span>}
              {selList.slice(0, MAX_CHIPS).map((c) => (
                <button key={c} className="ld-chip" disabled={scanning} title="remove" onClick={() => removeSelected(c)}>
                  {c} ×
                </button>
              ))}
              {selList.length > MAX_CHIPS && <span className="ld-dim">+{selList.length - MAX_CHIPS} more</span>}
            </div>

            <p className="ld-hint">
              run_scan reads every page of each selected client (fetch_chunk_size = rows per request) and fills
              pivot_matrix and client_summary. raw_log has its own client picker and page size.
            </p>
          </>
        )}
      </Panel>

      {/* ------------------------------------------------ scan log */}
      {scanOrder.length > 0 && (
        <Panel title="// SCAN_LOG">
          <div className="ld-term">
            {scanOrder.map((c) => {
              const s = scanStatus[c] || { state: 'queued', fetched: 0, total: 0 };
              const pct = s.total > 0 ? Math.round((s.fetched / s.total) * 100) : s.state === 'done' ? 100 : 0;
              const name = c.padEnd(8);
              if (s.state === 'queued') return <div key={c} className="ld-q">{`> ${name} queued`}</div>;
              if (s.state === 'aborted') return <div key={c} className="ld-q">{`> ${name} aborted`}</div>;
              if (s.state === 'error') return <div key={c} className="ld-err">{`> ${name} ERR  ${s.msg}`}</div>;
              const tail = s.state === 'done'
                ? `ok${s.unparsed ? `  (${s.unparsed} rows w/ unreadable logDate skipped)` : ''}`
                : 'fetching...';
              return (
                <div key={c} className={s.state === 'done' ? 'ld-ok' : 'ld-run'}>
                  {`> ${name} ${bar(s.fetched, s.total)} ${String(pct).padStart(3)}%  ${fmt(s.fetched)}/${fmt(s.total)}  ${tail}`}
                </div>
              );
            })}
          </div>
        </Panel>
      )}

      {/* ------------------------------------------------ tabs */}
      <div className="ld-tabs">
        <button className={`ld-tab${tab === 'pivot' ? ' on' : ''}`} onClick={() => setTab('pivot')}>pivot_matrix</button>
        <button className={`ld-tab${tab === 'clients' ? ' on' : ''}`} onClick={() => setTab('clients')}>client_summary</button>
        <button className={`ld-tab${tab === 'raw' ? ' on' : ''}`} onClick={() => setTab('raw')}>raw_log</button>
      </div>

      {/* ------------------------------------------------ shared summary strip */}
      {(tab === 'pivot' || tab === 'clients') && scanned.length > 0 && (
        <div className="ld-stats">
          <div className="ld-stat"><div className="ld-stat__k">total_logins</div><div className="ld-stat__v">{fmt(pivot.root.total)}</div></div>
          <div className="ld-stat"><div className="ld-stat__k">clients_scanned</div><div className="ld-stat__v">{scanned.length}</div></div>
          <div className="ld-stat"><div className="ld-stat__k">unique_users</div><div className="ld-stat__v">{fmt(totalUsers)}</div></div>
          <div className="ld-stat"><div className="ld-stat__k">active_months</div><div className="ld-stat__v">{pivot.leaves.length}</div></div>
          <div className="ld-stat">
            <div className="ld-stat__k">peak_month</div>
            <div className="ld-stat__v ld-stat__v--sm">{pivot.peak ? `${pivot.peak.full} (${fmt(pivot.peak.total)})` : '—'}</div>
          </div>
          <div className="ld-stat">
            <div className="ld-stat__k">date_span</div>
            <div className="ld-stat__v ld-stat__v--sm">{firstLeaf && lastLeaf ? `${firstLeaf.full} → ${lastLeaf.full}` : '—'}</div>
          </div>
        </div>
      )}

      {/* ------------------------------------------------ pivot */}
      {tab === 'pivot' && (
        <Panel title="// LOGIN_MATRIX">
          {scanned.length === 0 && (
            <EmptyState>
              {scanning
                ? 'scan in progress — each client appears here as soon as it finishes.'
                : 'No data yet. Choose clients in the dropdown, then run_scan.'}
            </EmptyState>
          )}

          {scanned.length > 0 && (
            <>
              <div className="ld-toolbar">
                <button className="term-btn term-btn--sm term-btn--ghost" onClick={() => setCollapsed(new Set())}>expand_all</button>
                <button className="term-btn term-btn--sm term-btn--ghost" onClick={() => setCollapsed(new Set(quarterIds))}>to_quarters</button>
                <button className="term-btn term-btn--sm term-btn--ghost" onClick={() => setCollapsed(new Set(yearIds))}>to_years</button>
                <button className="term-btn term-btn--sm" onClick={() => setNewestFirst((v) => !v)}>
                  order: {newestFirst ? 'newest first' : 'oldest first'}
                </button>
                <label className="ld-check">
                  <input type="checkbox" checked={hideEmpty} onChange={(e) => setHideEmpty(e.target.checked)} />
                  hide clients with 0 logins
                </label>
                <div className="ld-toolbar--end">
                  <button className="term-btn term-btn--sm" onClick={exportCsv}>export_csv</button>
                </div>
              </div>

              <div className="ld-tablewrap">
                <table className="ld-pivot" onMouseLeave={() => setHoverCol(-1)}>
                  <thead>
                    <tr>
                      <th />
                      <th className="grp" colSpan={Math.max(cols.length, 1)}>logins per client</th>
                      <th className="tot" />
                    </tr>
                    <tr>
                      <th>period</th>
                      {cols.map((c, i) => (
                        <th key={c} className={hoverCol === i ? 'is-hot' : ''}>{c}</th>
                      ))}
                      <th className="tot">total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {flatRows.map((n) => (
                      <tr key={n.id} className={`ld-l${n.level}`}>
                        <td style={{ paddingLeft: 12 + (n.level - 1) * 22 }}>
                          {n.kids.length ? (
                            <button className="ld-tog" onClick={() => toggleNode(n.id)}>
                              {collapsed.has(n.id) ? '[+]' : '[-]'}
                            </button>
                          ) : (
                            <span className="ld-tog ld-tog--leaf">└─</span>
                          )}
                          {n.label}
                          {n.level < 3 && <span className="ld-tag">subtotal</span>}
                        </td>
                        {cols.map((c, i) => {
                          const v = n.counts[c];
                          const heat = n.level === 3 && v && pivot.max
                            ? { backgroundColor: `rgba(var(--ld-heat), ${(0.06 + 0.42 * (v / pivot.max)).toFixed(3)})` }
                            : undefined;
                          return (
                            <td
                              key={c}
                              className={`num${hoverCol === i ? ' is-hot' : ''}`}
                              style={heat}
                              onMouseEnter={() => setHoverCol(i)}
                            >
                              {v ? fmt(v) : <span className="ld-dot">·</span>}
                            </td>
                          );
                        })}
                        <td className="num tot">
                          <div className="ld-bar">
                            <span className="ld-minibar"><i style={{ width: `${pctOf(n.total, pivot.levelMax[n.level])}%` }} /></span>
                            <span>{fmt(n.total)}</span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td>grand total</td>
                      {cols.map((c) => <td key={c} className="num">{fmt(pivot.root.counts[c])}</td>)}
                      <td className="num tot">{fmt(pivot.root.total)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              <div className="ld-legend">
                <span>bold rows = subtotals (year, quarter); click [-] / [+] to fold</span>
                <span><i className="ld-swatch" style={{ background: 'rgba(var(--ld-heat),.5)' }} />month cell shading = volume vs. busiest cell ({fmt(pivot.max)})</span>
                <span><span className="ld-dot">·</span> = no logins</span>
                <span>bars in total column = size vs. largest row of the same level</span>
              </div>
            </>
          )}
        </Panel>
      )}

      {/* ------------------------------------------------ client summary */}
      {tab === 'clients' && (
        <Panel title="// CLIENT_SUMMARY">
          {scanned.length === 0 && (
            <EmptyState>
              {scanning ? 'scan in progress — results appear as each client finishes.' : 'No data yet. Choose clients in the dropdown, then run_scan.'}
            </EmptyState>
          )}

          {scanned.length > 0 && (
            <div className="ld-tablewrap">
              <table className="ld-grid">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>client</th>
                    <th className="r">logins</th>
                    <th className="r">share</th>
                    <th className="r">unique users</th>
                    <th className="r">active months</th>
                    <th>first login</th>
                    <th>last login</th>
                    <th>busiest month</th>
                  </tr>
                </thead>
                <tbody>
                  {clientStats.map((s, i) => (
                    <tr key={s.code}>
                      <td className="id">{i + 1}</td>
                      <td><b>{s.code}</b></td>
                      <td className="r">
                        <div className="ld-bar">
                          <span className="ld-minibar"><i style={{ width: `${pctOf(s.total, clientStats[0].total)}%` }} /></span>
                          <span>{fmt(s.total)}</span>
                        </div>
                      </td>
                      <td className="r">{pctOf(s.total, pivot.root.total).toFixed(1)}%</td>
                      <td className="r">{fmt(s.users)}</td>
                      <td className="r">{s.months}</td>
                      <td>{s.first}</td>
                      <td>{s.last}</td>
                      <td>{s.peak}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td />
                    <td>total</td>
                    <td className="r">{fmt(pivot.root.total)}</td>
                    <td className="r">100%</td>
                    <td className="r">{fmt(totalUsers)}</td>
                    <td colSpan={4} />
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </Panel>
      )}

      {/* ------------------------------------------------ raw log */}
      {tab === 'raw' && (
        <Panel title="// RAW_LOGIN_LOG">
          <div className="ld-toolbar">
            <label className="ld-field" style={{ width: 210 }}>
              <span className="ld-field__label">client</span>
              <select
                className="ld-select"
                value={rawClient}
                disabled={rawLoading || !codes.length}
                onChange={(e) => changeRawClient(e.target.value)}
              >
                <option value="">— select client —</option>
                {codes.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </label>

            <label className="ld-field" style={{ width: 240 }}>
              <span className="ld-field__label">grep (this page)</span>
              <input
                className="ld-input"
                value={grep}
                placeholder="user, ip, status..."
                onChange={(e) => setGrep(e.target.value)}
              />
            </label>

            <button className="term-btn term-btn--sm" disabled={rawLoading || !rawClient} onClick={() => setReloadTick((t) => t + 1)}>
              reload
            </button>

            <span className="ld-dim ld-toolbar--end">
              {raw?.data?.[0]?.databaseName ? `db: ${raw.data[0].databaseName}   ` : ''}
              {rawRows.length ? `records ${fmt(rangeFrom)}–${fmt(rangeTo)} of ${fmt(totalRecords)}` : ''}
            </span>
          </div>

          {rawErr && <div className="ld-alert">! {rawErr}</div>}
          {!rawClient && <EmptyState>Select a client to read its login log.</EmptyState>}
          {rawClient && rawLoading && <Loader label="fetching login log" />}

          {rawClient && !rawLoading && !rawErr && rawRows.length === 0 && (
            <EmptyState>No login records returned for this page.</EmptyState>
          )}

          {rawClient && !rawLoading && rawRows.length > 0 && (
            <div className="ld-tablewrap">
              <table className="ld-grid">
                <thead>
                  <tr>
                    <th>logDate</th>
                    <th>username</th>
                    <th>employee</th>
                    <th>status</th>
                    <th>ip address</th>
                    <th className="r">userId</th>
                    <th className="r">branch</th>
                    <th className="r">loginId</th>
                  </tr>
                </thead>
                <tbody>
                  {shownRows.map((r) => (
                    <tr key={r.loginId}>
                      <td>{r.logDate}</td>
                      <td><b>{r.username}</b></td>
                      <td>{r.employeeName || <span className="ld-mono-dim">—</span>}</td>
                      <td>
                        <span className={`ld-badge ${isOk(r.logStatus) ? 'ld-ok' : 'ld-err'}`}>
                          {isOk(r.logStatus) ? 'success' : r.logStatus || 'unknown'}
                        </span>
                      </td>
                      <td>{r.ipAddress}</td>
                      <td className="r">{r.userId}</td>
                      <td className="r">{r.branchId}</td>
                      <td className="r id">{r.loginId}</td>
                    </tr>
                  ))}
                  {shownRows.length === 0 && (
                    <tr><td colSpan={8} className="ld-mono-dim">grep matched nothing on this page</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* pagination */}
          <div className="ld-pager">
            <div className="ld-pager__group">
              <NumField
                label="rows_per_page"
                value={pageSize}
                min={1}
                max={MAX_PAGE_SIZE}
                width={150}
                disabled={rawLoading}
                onCommit={changePageSize}
              />
              {PRESETS.map((p) => (
                <button
                  key={p}
                  className={`ld-preset${p === pageSize ? ' on' : ''}`}
                  disabled={rawLoading}
                  onClick={() => changePageSize(p)}
                >
                  {p}
                </button>
              ))}
            </div>

            <div className="ld-pager__group">
              <span className="ld-dim">page {page} of {fmt(totalPages)}</span>
              <button className="term-btn term-btn--sm" disabled={rawLoading || page <= 1} onClick={() => setPage(1)}>«</button>
              <button className="term-btn term-btn--sm" disabled={rawLoading || page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>prev</button>
              <button className="term-btn term-btn--sm" disabled={rawLoading || page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>next</button>
              <button className="term-btn term-btn--sm" disabled={rawLoading || page >= totalPages} onClick={() => setPage(totalPages)}>»</button>
              <NumField
                label="goto"
                value={page}
                min={1}
                max={totalPages}
                width={100}
                disabled={rawLoading || totalPages <= 1}
                onCommit={setPage}
              />
            </div>
          </div>
        </Panel>
      )}
    </div>
  );
}